import type { Hint } from "@scoach/types";
import { Chev } from "@scoach/ui/icons";
import { type CSSProperties, useEffect, useRef, useState } from "react";

import { HintCard } from "./HintCard.tsx";

/**
 * Renders one topic's hints as a single card with left/right navigation. A single
 * hint renders as a plain HintCard; multiple same-topic hints get a nav bar with
 * a count (so the rep sees there are more on this topic) and arrows to move
 * between the original and newer hints without them piling up the column.
 */
export function HintGroupCard({
  group,
  meetingId,
  style,
}: {
  group: Hint[];
  meetingId: string;
  style?: CSSProperties;
}) {
  const n = group.length;
  const [idx, setIdx] = useState(0); // 0 = original (oldest); higher = newer.
  const seenRef = useRef(1); // how many hints existed when the rep last looked.
  const [hasNew, setHasNew] = useState(false);

  // When new same-topic hints arrive, flag it (don't steal the rep's place).
  useEffect(() => {
    if (n > seenRef.current) setHasNew(true);
  }, [n]);

  // Keep the index in range if the group shrinks (e.g. threshold filter changes).
  useEffect(() => {
    if (idx > n - 1) setIdx(Math.max(0, n - 1));
  }, [n, idx]);

  if (n <= 1) {
    return <HintCard hint={group[0]!} meetingId={meetingId} style={style} />;
  }

  const safeIdx = Math.min(idx, n - 1);
  const cur = group[safeIdx]!;

  function go(next: number) {
    const clamped = Math.max(0, Math.min(n - 1, next));
    setIdx(clamped);
    seenRef.current = n;
    if (clamped === n - 1) setHasNew(false);
  }

  const arrowStyle: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 22,
    height: 22,
    borderRadius: 6,
  };

  return (
    <div style={style}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          padding: "4px 8px",
          marginBottom: 4,
          borderRadius: 8,
          background: "var(--bg-2)",
          fontSize: 12,
          color: "var(--text-3)",
        }}
      >
        <button
          type="button"
          className="icon-btn xs"
          style={arrowStyle}
          onClick={() => go(safeIdx - 1)}
          disabled={safeIdx === 0}
          title="Previous hint on this topic"
        >
          <Chev size={14} style={{ transform: "rotate(180deg)" }} />
        </button>

        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span className="mono">
            {safeIdx + 1} / {n}
          </span>
          <span>on this topic</span>
          {hasNew && (
            <span
              style={{
                background: "var(--gc-blue)",
                color: "#fff",
                fontSize: 10,
                fontWeight: 700,
                borderRadius: 10,
                padding: "1px 7px",
              }}
              title="New hint on this topic"
            >
              +{n - seenRef.current} new
            </span>
          )}
        </span>

        <button
          type="button"
          className="icon-btn xs"
          style={arrowStyle}
          onClick={() => go(safeIdx + 1)}
          disabled={safeIdx === n - 1}
          title="Next hint on this topic"
        >
          <Chev size={14} />
        </button>
      </div>

      <HintCard hint={cur} meetingId={meetingId} />
    </div>
  );
}
